import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SeoHead, organizationLd, webSiteLd, webPageLd } from '../seo';

interface CompetitorData {
  name: string;
  model: string;
  features: { name: string; parky: string; competitor: string }[];
  notes: string;
}

const COMPETITORS: Record<string, CompetitorData> = {
  'park-plus': {
    name: 'Park+',
    model: 'Aggregated monthly + hourly parking app. Operator commission 18–22%.',
    features: [
      { name: 'Listing fee', parky: 'Free', competitor: 'Free to list, 18–22% commission on bookings' },
      { name: 'Seeker signup', parky: 'None (anonymous)', competitor: 'Required (phone OTP)' },
      { name: 'Match radius', parky: '3 km, daily batch', competitor: 'Real-time, but only listed spots' },
      { name: 'Payment', parky: 'Direct host ↔ seeker', competitor: 'Aggregator processes' },
      { name: 'Price transparency', parky: 'Always visible', competitor: 'Listed but not surfaced' },
      { name: 'Public good', parky: 'Free map for all of India', competitor: 'Closed inventory per city' },
      { name: 'Two-wheeler support', parky: 'First-class', competitor: 'Secondary to car' },
      { name: 'EV charging', parky: 'Filter by EV', competitor: 'Limited filter' },
    ],
    notes:
      "Park+ is a strong incumbent with high brand recognition in Delhi NCR. Parksy is the open-data alternative: free, anonymous, no commission, and the map is the public good. For Delhi NCR operators, listing on both is fine. For operators in other metros, Parksy is the only free option that surfaces your spot to seekers actively looking.",
  },
  'nobroker-parking': {
    name: 'NoBroker Parking',
    model: 'Aggregator for monthly parking. Operator commission 15–20% on first month.',
    features: [
      { name: 'Listing fee', parky: 'Free', competitor: 'Free to list, 15–20% on first month' },
      { name: 'Seeker signup', parky: 'None (anonymous)', competitor: 'Required (phone OTP)' },
      { name: 'Match radius', parky: '3 km, daily batch', competitor: 'Citywide, lead-based' },
      { name: 'Payment', parky: 'Direct host ↔ seeker', competitor: 'NoBroker processes first month' },
      { name: 'Discovery model', parky: 'Open map for all', competitor: 'Lead-gen: pay per inquiry' },
      { name: 'Review model', parky: 'Community-driven', competitor: 'NoBroker moderated' },
      { name: 'Apartment focus', parky: 'General (commercial + residential)', competitor: 'Heavy apartment focus' },
      { name: 'Commission structure', parky: '0%', competitor: 'One-time 15–20% on first month' },
    ],
    notes:
      "NoBroker Parking is the largest Bangalore-focused aggregator for monthly parking. Their lead-gen model means operators pay for inquiries, regardless of conversion. Parksy's matching model means the cost is zero and the conversion intent is higher (seeker dropped a pin for that specific area). For Bangalore operators, listing on both is the right move; the NoBroker lead is useful, and the Parksy map surfaces you to in-area seekers.",
  },
  'justpark': {
    name: 'JustPark',
    model: 'Aggregator + SaaS for operators. Subscription-based visibility.',
    features: [
      { name: 'Listing fee', parky: 'Free', competitor: 'Subscription (₹5,000–₹50,000/year)' },
      { name: 'Seeker signup', parky: 'None (anonymous)', competitor: 'Required (phone OTP)' },
      { name: 'Booking fee', parky: '0%', competitor: '12–18% per booking' },
      { name: 'Operator SaaS', parky: 'No', competitor: 'Yes (booking management, hardware)' },
      { name: 'Map discoverability', parky: 'Public, crowdsourced', competitor: 'App-only' },
      { name: 'Price transparency', parky: 'Aggregated by city + locality', competitor: 'Per-spot only' },
      { name: 'Coverage', parky: 'Pan-India (12 cities + growing)', competitor: 'Mostly Bangalore, Delhi, Mumbai' },
      { name: 'Hardware integration', parky: 'No (community-moderated)', competitor: 'Yes (ANPR cameras, gates)' },
    ],
    notes:
      "JustPark's strength is operator SaaS — booking management, hardware, analytics. Parksy doesn't compete there. Where Parksy wins is the open data layer: any seeker can find any spot in any city without an app, and price data is surfaced at the city + locality level. Use JustPark for the booking-system side if you operate a large lot; use Parksy for the discovery + price-transparency side.",
  },
  'wall-clusters': {
    name: 'Wall Clusters',
    model: 'Tokenised parking marketplace. Operator commission on bookings.',
    features: [
      { name: 'Listing fee', parky: 'Free', competitor: 'Variable, often subscription' },
      { name: 'Seeker signup', parky: 'None (anonymous)', competitor: 'Required (email/phone)' },
      { name: 'Match model', parky: '3 km, daily batch', competitor: 'Real-time, app-only' },
      { name: 'Tokenisation', parky: 'No', competitor: 'Yes (parking slots as NFTs)' },
      { name: 'Map discoverability', parky: 'Public, crowdsourced', competitor: 'App + private API' },
      { name: 'Operational focus', parky: 'Discovery + matching', competitor: 'Investment product' },
      { name: 'Public good', parky: 'Yes (free map, free listing)', competitor: 'No (commission-driven)' },
      { name: 'Settlement', parky: 'Direct host ↔ seeker', competitor: 'Aggregator processes' },
    ],
    notes:
      "Wall Clusters is a newer entrant, focusing on tokenising parking as an investment product. The premise is interesting; the practical effect for seekers and operators is similar to a traditional aggregator. Parksy stays focused on the public-good angle: free map, free listing, zero commission. The two are complementary for operators who want to explore tokenisation; for pure discovery, Parksy is enough.",
  },
};

export default function Comparison() {
  const { slug = 'park-plus' } = useParams<{ slug: string }>();
  const data = useMemo(() => COMPETITORS[slug] ?? COMPETITORS['park-plus'], [slug]);
  const allSlugs = Object.keys(COMPETITORS);

  return (
    <>
      <SeoHead
        title={`Parksy vs ${data.name} — A Factual Feature Comparison | Parksy`}
        description={`Side-by-side comparison of Parksy and ${data.name}. Pricing, commission, seeker experience, public good angle, and which model fits your use case.`}
        canonical={`https://parksy.rent/vs/${slug}`}
        ogImage={`https://parksy.rent/og/vs-${slug}.png`}
        schemas={[organizationLd(), webSiteLd(), webPageLd({ name: `Parksy vs ${data.name}`, description: `Feature-by-feature comparison of Parksy and ${data.name}.`, url: `https://parksy.rent/vs/${slug}` })]}
      />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <header className="mb-6">
          <div className="text-xs uppercase tracking-wide text-parksy-700 font-semibold mb-2">Comparison</div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Parksy vs {data.name}</h1>
          <p className="text-lg text-gray-700">{data.model}</p>
        </header>

        <div className="bg-white border rounded-xl overflow-hidden mb-8">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2">Feature</th>
                <th className="text-left px-4 py-2 text-parksy-700">Parksy</th>
                <th className="text-left px-4 py-2">{data.name}</th>
              </tr>
            </thead>
            <tbody>
              {data.features.map((f) => (
                <tr key={f.name} className="border-t">
                  <td className="px-4 py-2 font-medium text-gray-900">{f.name}</td>
                  <td className="px-4 py-2 text-parksy-700">{f.parky}</td>
                  <td className="px-4 py-2 text-gray-700">{f.competitor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Summary</h2>
          <p className="text-gray-700 leading-relaxed">{data.notes}</p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Other comparisons</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            {allSlugs.map((s) => (
              <Link
                key={s}
                to={`/vs/${s}`}
                className={`bg-white border rounded-lg px-3 py-2 ${s === slug ? 'border-parksy-500 text-parksy-700' : 'text-gray-700 hover:border-parksy-500'}`}
              >
                vs {COMPETITORS[s].name}
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Try Parksy</h2>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-block bg-parksy-600 hover:bg-parksy-700 text-white font-medium px-5 py-2.5 rounded-lg"
            >
              Open the map →
            </Link>
            <Link
              to="/list-your-parking"
              className="inline-block bg-white border border-parksy-600 text-parksy-700 hover:bg-parksy-50 font-medium px-5 py-2.5 rounded-lg"
            >
              List your spot
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
